import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Loader2, Server } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'

export default function Login() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  // Return the user to where they were headed before the redirect, else the VMs page.
  const from = (location.state as { from?: string } | null)?.from || '/vms'

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await login(email.trim(), password)
      navigate(from, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell title="Sign in to VoltVisor" subtitle="Access your VMs">
      <form onSubmit={submit} className="space-y-4">
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        <Field label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" required />
        <Field label="Password" type="password" value={password} onChange={setPassword} autoComplete="current-password" required />
        <button
          type="submit"
          disabled={loading}
          className="w-full inline-flex items-center justify-center rounded-lg bg-voltainer-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-voltainer-700 focus:outline-none focus:ring-2 focus:ring-voltainer-500 focus:ring-offset-2 disabled:opacity-50"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          Sign in
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-gray-400">
        No account?{' '}
        <Link to="/signup" className="font-medium text-voltainer-400 hover:text-voltainer-300">
          Create one
        </Link>
      </p>
    </AuthShell>
  )
}

// --- shared auth UI (kept local so Login/Signup match without a new component file) ---
export function AuthShell({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center space-x-2 mb-8">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-voltainer-500 to-armored-500 flex items-center justify-center">
            <Server className="h-5 w-5 text-white" />
          </div>
          <span className="text-2xl font-bold text-white">VoltVisor</span>
        </div>
        <div className="rounded-2xl border border-gray-800 bg-gray-950/60 p-8 shadow-xl">
          <h1 className="text-lg font-semibold text-white">{title}</h1>
          <p className="mt-1 text-sm text-gray-400 mb-6">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  )
}

export function Field({
  label,
  type = 'text',
  value,
  onChange,
  autoComplete,
  required,
  hint,
}: {
  label: string
  type?: string
  value: string
  onChange: (v: string) => void
  autoComplete?: string
  required?: boolean
  hint?: string
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-300">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required={required}
        className="mt-1 w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-voltainer-500 focus:border-transparent"
      />
      {hint && <span className="mt-1 block text-[11px] text-gray-500">{hint}</span>}
    </label>
  )
}
